import { createFileRoute, notFound } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { TackCard } from "@/components/TackCard";
import { PoweredByTack } from "@/components/PoweredByTack";
import { toast } from "sonner";
import { getLocaleDefaults } from "@/lib/onboarding-defaults";
import { REDEEM_PAGE_STRINGS, SCAN_COMING_SOON, SCAN_PAUSED, type RedeemLocaleCode } from "@/lib/redeem-page-strings";
import { track } from "@/lib/track";
import { trackScanEvent } from "@/lib/track.functions";
import { loadTackMerchant } from "@/lib/tack-merchant.functions";
import { submitFeedback, resendRewardSMS } from "@/lib/feedback.functions";
import { trackPlausible } from "@/lib/plausible";

export const Route = createFileRoute("/tack/$slug")({
  // SSR loader — page arrives with merchant data already in HTML.
  // No spinner, no client-side waterfall. The $slug param actually contains
  // both the human-readable slug AND the secret scan_token, joined by "-"
  // (e.g. "joes-pizza-k7p2x9"). The loader splits and verifies both.
  loader: async ({ params }) => {
    const { merchant, scanEnabled, preTrial } = await loadTackMerchant({
      data: { slugWithToken: params.slug },
    });
    if (!merchant) throw notFound();
    return { merchant, scanEnabled, preTrial };
  },
  component: FeedbackPage,
  notFoundComponent: () => {
    const lang = typeof navigator !== "undefined" && navigator.language?.toLowerCase().startsWith("sv") ? "sv" : "en";
    const L = REDEEM_PAGE_STRINGS[lang as RedeemLocaleCode] ?? REDEEM_PAGE_STRINGS.en;
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center">
          <h1 className="text-2xl font-display font-bold mb-2">{L.notFoundHeader ?? "Not Found"}</h1>
          <p className="text-muted-foreground text-sm">{L.notFoundBody ?? "This business page doesn't exist."}</p>
        </div>
        <PoweredByTack />
      </div>
    );
  },
});

type Step = "form" | "submitted" | "pending_review" | "has_active_code" | "recent_scan" | "cooldown";

function FeedbackPage() {
  const { merchant, scanEnabled, preTrial } = Route.useLoaderData();
  const [step, setStep] = useState<Step>("form");
  const [blocked, setBlocked] = useState(!scanEnabled);
  const [rating, setRating] = useState(0);
  const [phone, setPhone] = useState("");
  const [consentTx, setConsentTx] = useState(false);
  const [consentMkt, setConsentMkt] = useState(false);
  const [freeText, setFreeText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [activePromoCode, setActivePromoCode] = useState<string | null>(null);
  const [activePromoExpiresAt, setActivePromoExpiresAt] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  // Reward-quota cap is intentionally silent — never surface an unlock date to the customer.

  // Read ?session=<id> and optional ?lang=<code> from the URL once on mount.
  // ?lang is used by the /demo flow so the scanned page renders in the
  // visitor's chosen language regardless of the merchant's stored locale.
  const [langOverride, setLangOverride] = useState<string | null>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session");
    if (sid) setSessionId(sid);
    const lang = params.get("lang");
    if (lang === "sv" || lang === "en") setLangOverride(lang);
  }, []);


  // Fire-and-forget scan tracking — runs once after mount.
  // useEffect with empty deps + window-level guard prevents StrictMode double-fire
  // and remount duplicates. Server has its own 30-min dedupe as belt-and-braces.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if ((window as any).__tack_scan_logged) return;
    (window as any).__tack_scan_logged = true;
    const sid = sessionStorage.getItem("tack_session_id") || (() => {
      const id = crypto.randomUUID();
      sessionStorage.setItem("tack_session_id", id);
      return id;
    })();
    trackScanEvent({
      data: { merchantId: merchant.id, sessionId: sid, slug: merchant.id },
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRating = (value: number) => {
    setRating(value);
    track("rating_selected", merchant.id, { rating: value }, { requireConsent: true });
  };

  const effectiveLocale = (langOverride ?? merchant.locale) as string;
  const S = REDEEM_PAGE_STRINGS[effectiveLocale as RedeemLocaleCode] ?? REDEEM_PAGE_STRINGS.en;


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rating || !phone.trim()) return;
    if (!consentTx) {
      toast.error(S.consentRequired ?? "Please agree to receive your discount by text.");
      return;
    }

    setSubmitting(true);

    try {
      const result = await submitFeedback({
        data: {
          merchantId: merchant.id,
          starRating: rating,
          phone: phone.trim(),
          transactionalConsent: consentTx,
          marketingConsent: consentMkt,
          customerComment: freeText || null,
          sessionId: sessionId || null,
          locale: effectiveLocale === "sv" ? "sv" : "en",
        },
      });

      if (!result.ok) {
        if (result.reason === "has_active_code") {
          setActivePromoCode(result.promoCode);
          setActivePromoExpiresAt(result.promoExpiresAt);
          setStep("has_active_code");
          track("active_code_shown", merchant.id, undefined, { requireConsent: true });
        } else if (result.reason === "not_activated") {
          // Merchant lapsed/never activated between page load and submit —
          // show the same friendly "coming soon" screen instead of an error.
          setBlocked(true);
        } else if (result.reason === "cooldown") {
          // Silent VIP cap — do NOT capture or display result.unlockAt.
          setStep("cooldown");
          track("cooldown_blocked", merchant.id, undefined, { requireConsent: true });
        } else if (result.reason === "rate_limited") {
          toast.error(S.tooManySubmissions ?? "Too many submissions from this network. Please try again later.");
        } else {
          toast.error(S.submitError ?? "Something went wrong. Please try again.");
        }
        setSubmitting(false);
        return;
      }

      track("email_submitted", merchant.id, { comment_length: freeText?.length || 0 }, { requireConsent: true });
      trackPlausible("Feedback Submitted", { rating });
      setActivePromoCode(result.promoCode);
      setStep(result.pending ? "pending_review" : "submitted");
    } catch {
      toast.error(S.submitError ?? "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!activePromoCode || !phone.trim()) {
      toast.info(S.saveFromScreen ?? "Save your code from this screen.");
      return;
    }
    setResending(true);
    try {
      const result = await resendRewardSMS({
        data: {
          merchantId: merchant.id,
          phone: phone.trim(),
          promoCode: activePromoCode,
          locale: effectiveLocale === "sv" ? "sv" : "en",
        },
      });
      if (result.ok) {
        toast.success(S.resendSent ?? "Text sent — check your phone.");
      } else if (result.reason === "limit_reached") {
        toast.error(S.resendLimit ?? "Resend limit reached. Save the code from this screen.");
      } else if (result.reason === "too_soon") {
        toast.info(S.resendTooSoon ?? "Give it a moment before trying again.");
      } else {
        toast.error(S.resendFailed ?? "Couldn't resend. Save the code from this screen.");
      }
    } catch {
      toast.error(S.resendFailed ?? "Couldn't resend. Save the code from this screen.");
    } finally {
      setResending(false);
    }
  };


  const L = getLocaleDefaults(effectiveLocale);

  // Merchant hasn't activated their rewards account (or it lapsed): don't show
  // the feedback form at all — no orphan code, no phone number captured.
  if (blocked) {
    return (
      <ScanComingSoon
        paused={!preTrial}
        businessName={merchant.business_name}
        locale={effectiveLocale}
        logoUrl={merchant.brand_logo_url}
        bgColor={merchant.brand_bg_color}
        textColor={merchant.brand_text_color}
        accentColor={merchant.brand_accent_color || merchant.brand_primary_color}
      />
    );
  }

  return (
    <div className="min-h-screen">
      <TackCard
        merchant={{
          ...(merchant as any),
          locale: effectiveLocale,
          feedback_prompt: merchant.feedback_prompt || L.feedbackPrompt,
        }}
        step={step}
        rating={rating}
        interactive
        onRate={handleRating}
        onSubmitEmail={handleSubmit}
        phone={phone}
        onPhoneChange={setPhone}
        consentTransactional={consentTx}
        onConsentTransactionalChange={setConsentTx}
        consentMarketing={consentMkt}
        onConsentMarketingChange={setConsentMkt}
        phoneLabelText={L.phoneLabel}
        phonePlaceholderText={L.phonePlaceholder}
        consentTransactionalText={L.consentTransactional}
        consentMarketingText={L.consentMarketing}
        freeText={freeText}
        onFreeTextChange={setFreeText}
        submitting={submitting}
        promoCode={activePromoCode || undefined}
        promoExpiresAt={activePromoExpiresAt}
        onResend={handleResend}
        resending={resending}
        // cooldownUnlockAt intentionally not passed — silent VIP cap.
        // Merchant overrides take precedence; otherwise fall back to locale defaults.
        emailLabel={merchant.email_label || L.emailLabel}
        ctaText={merchant.cta_text || L.getDiscountCta.replace("{X}", String(merchant.promo_discount_percent))}
        thankYouText={merchant.thank_you_text || L.thankYou}
        discountCodeLabel={merchant.discount_code_label || L.discountCodeLabel}
        discountSubtext={merchant.discount_subtext || L.discountSubtext.replace("{X}", String(merchant.promo_discount_percent))}
        redeemButtonText={merchant.redeem_button_text || L.redeemButton}
        saveCodeHint={merchant.save_code_hint || L.saveCodeHint}
        activatesInText={merchant.activates_in_text || L.activatesIn}
        emailWhenReadyText={merchant.email_when_ready_text || L.emailWhenReady.replace("{X}", String(merchant.promo_discount_percent))}
        optionalPlaceholder={merchant.feedback_optional_text || L.feedbackOptional}
        showCashierText={merchant.show_cashier_text || L.showCashier}
        validUntilText={merchant.valid_until_text || L.validUntil}
        checkInboxText={merchant.check_inbox_text || L.checkInbox}
        cooldownText={L.cooldownBody}
        submittedSentText={L.submittedSent.replace("{X}", String(merchant.promo_discount_percent))}
        submittedClaimText={L.submittedClaim}
        submittedSpamText={L.submittedSpam}
        startButtonText={L.startButton}
        startingButtonText={L.startingButton}
        readyExplainerText={L.readyExplainer}
        readyValidForDaysTemplate={L.readyValidForDays}
        readyValidUntilTomorrowText={L.readyValidUntilTomorrow}
        hourLabel={L.hour}
        hoursLabel={L.hours}
        cooldownHeaderText={L.cooldownHeader}
        welcomeBackHeaderText={L.welcomeBackHeader}
        justAMomentHeaderText={L.justAMomentHeader}
        pendingReviewThanksText={L.pendingReviewThanks}
        pendingReviewBodyTemplate={L.pendingReviewBody}
        recentScanHeaderText={L.recentScanHeader}
        recentScanBodyText={L.recentScanBody}
        hasActiveCodeHintText={L.hasActiveCodeHint}
        resendToEmailButtonText={L.resendToEmailButton}
        smsSentHeaderText={L.smsSentHeader}
        smsSentBodyText={L.smsSentBody}
        sendingShortText={L.sendingShort}
        codeLabelInlineText={L.codeLabelInline}
        submittingButtonText={L.submittingButton}
        emailPlaceholderText={L.emailPlaceholder}
        demoMode={!!sessionId}
        demoBannerTitleText={S.demoBannerTitle}
        demoBannerBodyText={S.demoBannerBody}
        welcomeFallbackText={L.welcomeText}
      />
      <p className="mx-auto max-w-md px-4 pb-6 text-[11px] text-muted-foreground text-center">
        {(REDEEM_PAGE_STRINGS[effectiveLocale as RedeemLocaleCode] ?? REDEEM_PAGE_STRINGS.en).somethingNotWorking}{" "}
        <a
          href="mailto:hello@tack.company"
          className="underline underline-offset-4 hover:text-foreground"
        >
          hello@tack.company
        </a>
      </p>
    </div>
  );
}

/**
 * Shown instead of the feedback form when the business hasn't activated their
 * rewards account yet. Keeps their brand colours/logo so it reads as
 * "not quite live yet", never as a broken or suspended page.
 */
function ScanComingSoon({
  paused,
  businessName,
  locale,
  logoUrl,
  bgColor,
  textColor,
  accentColor,
}: {
  paused: boolean;
  businessName: string;
  locale: string;
  logoUrl?: string | null;
  bgColor?: string | null;
  textColor?: string | null;
  accentColor?: string | null;
}) {
  const C = SCAN_COMING_SOON[locale as RedeemLocaleCode] ?? SCAN_COMING_SOON.en;
  const P = SCAN_PAUSED[locale as RedeemLocaleCode] ?? SCAN_PAUSED.en;
  const header = paused ? P.header : C.header;
  const body = paused ? P.body(businessName) : C.body(businessName);
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6 py-12 text-center"
      style={{ backgroundColor: bgColor || undefined, color: textColor || undefined }}
    >
      <div className="max-w-sm w-full space-y-5">
        {logoUrl ? (
          <img src={logoUrl} alt={businessName} className="h-14 mx-auto object-contain" />
        ) : (
          <p className="text-sm font-semibold tracking-wide opacity-70">{businessName}</p>
        )}
        <div
          className="mx-auto flex size-14 items-center justify-center rounded-full text-2xl"
          style={{ backgroundColor: `${accentColor || "#D47B5E"}20` }}
        >
          {paused ? "⏸️" : "✨"}
        </div>
        <h1 className="text-2xl font-display font-bold">{header}</h1>
        <p className="text-sm opacity-80">{body}</p>
        <p className="text-sm font-medium" style={{ color: accentColor || undefined }}>
          {C.scanAgain}
        </p>
      </div>
      <PoweredByTack />
    </div>
  );
}
